import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MappingKind } from '../../api/types';
import { kindDescriptionKeys, kindGuideKeys } from './mappingKinds';

const collapsedStorageKey = 'algobridge.mappings.guideCollapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(collapsedStorageKey) === 'true';
  } catch {
    return false;
  }
}

function storeCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(collapsedStorageKey, String(collapsed));
  } catch {
    // A browser that refuses storage only forgets the preference between visits.
  }
}

/** One example row of a kind's guide: the tenant's code, the standard code it becomes and what that code means. */
interface GuideExample {
  source: string;
  target: string;
  meaning: string;
}

/**
 * What one kind of mapping translates, in the tenant user's own terms: which field of the invoice it
 * is, what their system tends to write there, what the standard list offers, what happens with no row
 * at all, and a few worked examples. Sits under the kind tabs so the answer is next to the question.
 */
export function MappingKindGuide({ kind }: { kind: MappingKind }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const base = kindGuideKeys[kind];
  const examples = t(`${base}.examples`, { returnObjects: true }) as GuideExample[];

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    storeCollapsed(next);
  };

  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t(kindDescriptionKeys[kind])}
        </Typography>
        <Button
          size="small"
          onClick={toggle}
          endIcon={collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          sx={{ flexShrink: 0 }}
        >
          {collapsed ? t('mappings.kindGuide.show') : t('mappings.kindGuide.hide')}
        </Button>
      </Box>

      <Collapse in={!collapsed}>
        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' },
          }}
        >
          <Stack spacing={1.5}>
            <GuideSection title={t('mappings.kindGuide.sections.what')} text={t(`${base}.what`)} />
            <GuideSection title={t('mappings.kindGuide.sections.source')} text={t(`${base}.source`)} />
            <GuideSection title={t('mappings.kindGuide.sections.target')} text={t(`${base}.target`)} />
            <GuideSection title={t('mappings.kindGuide.sections.default')} text={t(`${base}.default`)} />
          </Stack>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('mappings.kindGuide.sections.examples')}
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('mappings.kindGuide.columns.source')}</TableCell>
                    <TableCell>{t('mappings.kindGuide.columns.target')}</TableCell>
                    <TableCell>{t('mappings.kindGuide.columns.meaning')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {examples.map((example) => (
                    <TableRow key={example.source}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{example.source}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {example.target}
                      </TableCell>
                      <TableCell>{example.meaning}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

function GuideSection({ title, text }: { title: string; text: string }) {
  return (
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
}
